/**
 * calendarItems.js — Moteur partagé du calendrier Esprit Padel.
 *
 * Source UNIQUE de l'agrégation multi-sources (événements, tâches, publications,
 * réunions, campagnes, demandes, Metricool, tournois, événements comm) et des
 * droits d'accès par club. Extrait de CalendrierV2 pour être réutilisé par le
 * dashboard (via useCalendarItems) sans dupliquer la logique.
 *
 * Droits : admin (user.admin === true) voit tout ; sinon, ses clubs
 * (user.clubs) + les éléments nationaux (sans club, ou club « France »).
 */

/* ── Pastel color tokens per source type ── */
export const C = {
  tournoi:     { bg: "#CCFBF1", text: "#115E59", border: "#5EEAD4" },
  event:       { bg: "#BFDBFE", text: "#1E3A8A", border: "#93C5FD" },
  commevent:   { bg: "#FECACA", text: "#7F1D1D", border: "#FCA5A5" },
  publication: { bg: "#FED7AA", text: "#7C2D12", border: "#FDBA74" },
  meeting:     { bg: "#BBF7D0", text: "#064E3B", border: "#86EFAC" },
  task:        { bg: "#FDE68A", text: "#78350F", border: "#FCD34D" },
  project:     { bg: "#FBCFE8", text: "#9D174D", border: "#F9A8D4" },
  request:     { bg: "#C7D2FE", text: "#3730A3", border: "#A5B4FC" },
  metricool:   { bg: "#E9D5FF", text: "#581C87", border: "#D8B4FE" },
};

export const SOURCE_LABELS = {
  tournoi:      "Tournois",
  event:        "Agenda",
  commevent:    "Événements comm",
  publication:  "Publications",
  meeting:      "Réunions",
  task:         "Tâches",
  project:      "Campagnes",
  request:      "Demandes clubs",
  metricool:    "Metricool",
  marketing360: "Marketing 360°",
};

export const FILTER_ORDER = ["tournoi","event","commevent","publication","meeting","task","project","request","metricool"];

/* Route mapping — sourceType → pageId V2 (seules les routes réellement branchées). */
export const ROUTE_MAP = {
  request: "demandes",
  project: "campagnes",
};

/* ── Date utils ── */
export const FR_DAYS   = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
export const FR_MONTHS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

export function startOfDay(d)   { const r = new Date(d); r.setHours(0,0,0,0); return r; }
export function addDays(d, n)   { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
export function addMonths(d, n) { const r = new Date(d); r.setMonth(r.getMonth() + n); return r; }
export function isSameDay(a, b) { return a && b && a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate(); }
export function fmtISO(d)       { const r = new Date(d); r.setMinutes(r.getMinutes() - r.getTimezoneOffset()); return r.toISOString().slice(0,10); }
export function fmtDayFull(d)   { return `${FR_DAYS[(d.getDay()+6)%7]} ${d.getDate()} ${FR_MONTHS[d.getMonth()]} ${d.getFullYear()}`; }

export function parseDate(s) {
  if (!s) return null;
  if (s instanceof Date) return isNaN(s.getTime()) ? null : s;
  if (s && typeof s === "object" && s.toDate) return s.toDate();
  if (s && typeof s === "object" && s.seconds) return new Date(s.seconds * 1000);
  const d = new Date(typeof s === "string" && s.length <= 10 ? s + "T00:00:00" : s);
  return isNaN(d.getTime()) ? null : d;
}

export function getWeekStart(d) {
  const r = startOfDay(d);
  const day = r.getDay();
  r.setDate(r.getDate() + (day === 0 ? -6 : 1 - day));
  return r;
}

export function getMonthGridDates(year, month) {
  const first = new Date(year, month, 1);
  const start = getWeekStart(first);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

export function itemCoversDay(item, d) {
  if (!item.date) return false;
  const s = startOfDay(item.date);
  const t = startOfDay(d);
  if (item.endDate) {
    const e = startOfDay(item.endDate);
    return t >= s && t <= e;
  }
  return isSameDay(s, t);
}

/* ── Club resolution (multi-valeurs) ──
 * Lit item.clubs (tableau) puis fallback item.club / item.clubId.
 * Renvoie TOUJOURS un tableau de { id, name, color } (vide = national). */
export function resolveClubs(item, clubs) {
  const raw = Array.isArray(item.clubs)
    ? item.clubs
    : (item.club != null ? [item.club] : item.clubId != null ? [item.clubId] : []);
  const out = [];
  for (const cid of raw) {
    if (cid === null || cid === undefined || cid === "") continue;
    const match = (clubs || []).find(x => String(x.id) === String(cid));
    if (match) out.push({ id: match.id, name: match.name || match.nom, color: match.color || null });
    else out.push({ id: cid, name: String(cid), color: null });
  }
  return out;
}

/* Résout un clubName/clubId libre (tournois, commevents) en { id, name, color }. */
/* Alias de clubs : certains noms de l'app Tournois diffèrent de ep:clubs.
 * (Esprit Padel Saint-Priest correspond au club « Lyon ».) */
const CLUB_ALIASES = { "saint priest": "lyon", "saint-priest": "lyon" };

export function resolveClubByNameOrId(nameOrId, clubs) {
  if (!nameOrId) return null;
  const s = String(nameOrId);
  const sl = s.toLowerCase();
  let m = (clubs || []).find(x => String(x.id) === s || String(x.name || x.nom).toLowerCase() === sl);
  if (!m && CLUB_ALIASES[sl]) {
    const target = CLUB_ALIASES[sl];
    m = (clubs || []).find(x => String(x.name || x.nom).toLowerCase() === target);
  }
  if (m) return { id: m.id, name: m.name || m.nom, color: m.color || null };
  return { id: s, name: s, color: null };
}

/* Couleur par club — sert à différencier les tournois par club.
 * Utilise la couleur définie du club, sinon une couleur déterministe stable
 * (même club → toujours la même couleur, y compris pour les clubs venant de
 * l'app Tournois et absents de ep:clubs). */
const CLUB_PALETTE = ["#0071CD","#00A878","#EC4899","#F59E0B","#7C3AED","#EF4444","#0891B2","#65A30D"];
export function clubHashColor(key) {
  const s = String(key || "autre");
  let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return CLUB_PALETTE[h % CLUB_PALETTE.length];
}
function hexLum(hex) {
  const h = String(hex).replace("#", "");
  if (h.length < 6) return 0.5;
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}
/* Teinte marquée par club : fond soutenu + texte contrasté (lisible sur clair ET foncé). */
export function clubColorTokens(hex) {
  const light = hexLum(hex) > 0.55;
  return { bg: hex + (light ? "59" : "40"), text: light ? "#3F2D00" : hex, border: hex };
}

/*
 * User resolution — résout un ID (appId, uid, id numérique) en nom lisible.
 * ID technique long sans correspondance → null (jamais d'ID brut affiché).
 */
export function resolveUser(rawId, users) {
  if (!rawId) return null;
  const sid = String(rawId).trim();
  if (!sid || sid === "guest") return null;
  if (users && users.length) {
    const u = users.find(x =>
      String(x.id)    === sid ||
      String(x.appId) === sid ||
      String(x.uid)   === sid
    );
    if (u) {
      const name = u.name || u.displayName || [u.firstName, u.lastName].filter(Boolean).join(" ") || [u.prenom, u.nom].filter(Boolean).join(" ") || u.email;
      return name || null;
    }
  }
  if (/^\d{10,}(\.\d+)?$/.test(sid)) return null; // ID technique sans correspondance
  return sid; // chaîne déjà lisible
}

export function resolveUserList(ids, users) {
  if (!ids) return null;
  const list = Array.isArray(ids) ? ids : [ids];
  const names = list.map(id => resolveUser(id, users)).filter(Boolean);
  return names.length > 0 ? names.join(", ") : null;
}

/* Nom canonique d'un utilisateur ep:users. */
function canonicalUserName(u) {
  return u.name || u.displayName
    || [u.firstName, u.lastName].filter(Boolean).join(" ")
    || [u.prenom, u.nom].filter(Boolean).join(" ")
    || u.email || null;
}

/*
 * Résolution STRICTE pour le filtre par personne : ne renvoie un nom que si la
 * valeur correspond à un compte ep:users réel — soit par id/appId/uid, soit par
 * nom canonique / email (pour canoniser les valeurs saisies en texte libre).
 * Les valeurs libres sans correspondance (ex. "guest", surnoms) sont écartées,
 * évitant les quasi-doublons dans le filtre.
 */
function resolveUserStrict(rawId, users) {
  if (!rawId || !users || !users.length) return null;
  const sid = String(rawId).trim();
  if (!sid || sid.toLowerCase() === "guest") return null;
  let u = users.find(x =>
    String(x.id) === sid || String(x.appId) === sid || String(x.uid) === sid
  );
  if (!u) {
    const low = sid.toLowerCase();
    u = users.find(x => {
      const nm = canonicalUserName(x);
      return (nm && nm.toLowerCase() === low) || String(x.email || "").toLowerCase() === low;
    });
  }
  return u ? canonicalUserName(u) : null;
}

/* Personnes « chargées » d'un item — uniquement des comptes ep:users reconnus. */
export function extractPeople(meta, users) {
  const names = new Set();
  const add = (v) => { const n = resolveUserStrict(v, users); if (n) names.add(n); };
  ["owner","createdBy","responsable","assignee","assigneeId"].forEach(k => { if (meta[k] != null) add(meta[k]); });
  ["assignedTo","participants","members"].forEach(k => {
    const v = meta[k];
    if (Array.isArray(v)) v.forEach(add); else if (v != null) add(v);
  });
  return [...names];
}

/* ════════════════════════════════════════════════════════
   NORMALIZERS
   ════════════════════════════════════════════════════════ */

export function normalizeEvents(events, clubs, users) {
  return (events || []).filter(Boolean).map(e => {
    const d = parseDate(e.date || e.startDate || e.start);
    if (!d) return null;
    return {
      id: `event-${e.id || Math.random()}`, sourceType: "event", sourceId: e.id,
      title: e.title || e.nom || e.name || "Événement",
      date: d, endDate: parseDate(e.endDate || e.end) || null,
      time: e.time || e.startTime || null, endTime: e.endTime || null,
      allDay: !e.time && !e.startTime, color: C.event,
      clubs: resolveClubs(e, clubs), people: extractPeople(e, users),
      status: e.status || null, meta: e,
    };
  }).filter(Boolean);
}

export function normalizeTasks(tasks, clubs, users) {
  return (tasks || []).filter(Boolean).map(t => {
    const d = parseDate(t.dueDate || t.deadline || t.date);
    if (!d) return null;
    return {
      id: `task-${t.id || Math.random()}`, sourceType: "task", sourceId: t.id,
      title: t.title || t.name || t.label || "Tâche",
      date: d, endDate: null,
      time: t.time || null, endTime: null,
      allDay: !t.time, color: C.task,
      clubs: resolveClubs(t, clubs), people: extractPeople(t, users),
      status: t.status || null, meta: t,
    };
  }).filter(Boolean);
}

export function normalizePublications(publications, clubs, users) {
  return (publications || []).filter(Boolean).map(p => {
    const d = parseDate(p.scheduledDate || p.publishDate || p.scheduledAt || p.date);
    if (!d) return null;
    const text = p.text || p.caption || "";
    return {
      id: `pub-${p.id || Math.random()}`, sourceType: "publication", sourceId: p.id,
      title: p.title || (text.slice(0, 40) + (text.length > 40 ? "…" : "")) || "Publication",
      date: d, endDate: null,
      time: p.time || null, endTime: null,
      allDay: !p.time, color: C.publication,
      clubs: resolveClubs(p, clubs), people: extractPeople(p, users),
      status: p.status || p.statut || null, meta: p,
    };
  }).filter(Boolean);
}

export function normalizeMeetings(meetings, clubs, users) {
  return (meetings || []).filter(Boolean).map(m => {
    const d = parseDate(m.date || m.startDate || m.start);
    if (!d) return null;
    return {
      id: `meeting-${m.id || Math.random()}`, sourceType: "meeting", sourceId: m.id,
      title: m.title || m.subject || m.objet || "Réunion",
      date: d, endDate: parseDate(m.endDate || m.end) || null,
      time: m.time || m.startTime || null, endTime: m.endTime || null,
      allDay: !m.time && !m.startTime, color: C.meeting,
      clubs: resolveClubs(m, clubs), people: extractPeople(m, users),
      status: m.status || null, meta: m,
    };
  }).filter(Boolean);
}

export function normalizeMetricool(data, clubs, users) {
  if (!data) return [];
  const items = Array.isArray(data) ? data : (data.items || []);
  return items.filter(Boolean).map(item => {
    const rawDate = item.publicationDate?.dateTime || item.publicationDate?.date || item.publicationDate;
    const d = parseDate(rawDate);
    if (!d) return null;
    const hasTime = !!(item.publicationDate?.dateTime);
    return {
      id: `metricool-${item.id || Math.random()}`, sourceType: "metricool", sourceId: item.id,
      title: (item.text || item.title || "Post").slice(0, 60),
      date: d, endDate: null,
      time: hasTime ? d.toTimeString().slice(0, 5) : null, endTime: null,
      allDay: !hasTime, color: C.metricool,
      clubs: item.club ? resolveClubs({ club: item.club }, clubs) : [], people: extractPeople(item, users),
      status: item.status || null, meta: item,
    };
  }).filter(Boolean);
}

/* Campagnes — ep:campagnes (dateStart → dateEnd). */
export function normalizeCampagnes(campagnes, clubs, users) {
  return (campagnes || []).filter(Boolean).map(c => {
    const start = parseDate(c.dateStart || c.startDate || c.start);
    const end   = parseDate(c.dateEnd || c.endDate || c.deadline);
    if (!start && !end) return null;
    const date = start || end;
    const endDate = start && end ? end : null;
    return {
      id: `project-${c.id || Math.random()}`, sourceType: "project", sourceId: c.id,
      title: c.name || c.title || c.nom || "Campagne",
      date, endDate, time: null, endTime: null,
      allDay: true, color: C.project,
      clubs: resolveClubs(c, clubs), people: extractPeople(c, users),
      status: c.published ? "Publiée" : "Brouillon", meta: c,
    };
  }).filter(Boolean);
}

/* Demandes clubs — ep:requests (deadline explicite). */
export function normalizeRequests(requests, clubs, users) {
  return (requests || []).filter(Boolean).map(r => {
    const deadline = parseDate(r.deadline || r.dueDate);
    if (!deadline) return null;
    return {
      id: `request-${r.id || Math.random()}`, sourceType: "request", sourceId: r.id,
      title: r.subject || r.titre || r.title || r.objet || "Demande",
      date: deadline, endDate: null, time: null, endTime: null,
      allDay: true, color: C.request,
      clubs: resolveClubs(r, clubs), people: extractPeople(r, users),
      status: r.status || r.statut || null, meta: r,
    };
  }).filter(Boolean);
}

/* Tournois — API externe (lecture seule). */
export function normalizeTournaments(tournaments, clubs) {
  return (tournaments || []).filter(Boolean).map(t => {
    const d = parseDate(t.date);
    if (!d) return null;
    const club = resolveClubByNameOrId(t.clubName || t.clubId, clubs);
    return {
      id: `tournoi-${t.id || Math.random()}`, sourceType: "tournoi", sourceId: t.id,
      title: [t.level, t.name || "Tournoi"].filter(Boolean).join(" · "),
      date: d, endDate: parseDate(t.dateFin || t.endDate) || null,
      time: t.heureDebut || null, endTime: t.heureFin || null,
      allDay: !t.heureDebut,
      color: club ? clubColorTokens(club.color || clubHashColor(club.name || club.id)) : C.tournoi,
      clubs: club ? [club] : [], people: [],
      status: t.status || null, meta: t,
    };
  }).filter(Boolean);
}

/* Événements ponctuels comm — ep:commevents. */
export function normalizeCommEvents(commEvents, clubs, users) {
  return (commEvents || []).filter(Boolean).map(e => {
    const d = parseDate(e.date);
    if (!d) return null;
    const club = e.clubId ? resolveClubByNameOrId(e.clubId, clubs) : null;
    return {
      id: `commevent-${e.id || Math.random()}`, sourceType: "commevent", sourceId: e.id,
      title: e.title || "Événement",
      date: d, endDate: null,
      time: e.time || null, endTime: e.endTime || null,
      allDay: !e.time, color: C.commevent,
      clubs: club ? [club] : [], people: extractPeople(e, users),
      status: null, meta: e,
    };
  }).filter(Boolean);
}

/* ── Accès par rôle ── */
export function isNational(item) {
  if (!item.clubs || item.clubs.length === 0) return true;
  return item.clubs.some(c => String(c.name || "").toLowerCase() === "france");
}
export function visibleToUser(item, { isAdmin, userClubIds, appId }) {
  if (isAdmin) return true;
  // Événement agenda privé ("moi seul") → uniquement son propriétaire
  if (item.sourceType === "event" && item.meta?.visibility === "me") {
    return String(item.meta.owner) === String(appId);
  }
  if (isNational(item)) return true;
  return item.clubs.some(c => userClubIds.has(String(c.id)));
}

/* ── Filtres d'affichage ── */
export function applyFilters(items, selectedClubs, selectedSources, selectedPeople) {
  return items.filter(item => {
    if (selectedSources.size > 0 && !selectedSources.has(item.sourceType)) return false;
    if (selectedClubs.size > 0) {
      const inClub = item.clubs.some(c => selectedClubs.has(String(c.id)));
      if (!inClub && !isNational(item)) return false;
    }
    if (selectedPeople.size > 0) {
      if (!item.people || !item.people.some(p => selectedPeople.has(p))) return false;
    }
    return true;
  });
}

/* ── Shared constants (vues semaine / jour) ── */
export const HOUR_START = 7;
export const HOUR_END   = 22;
export const HOUR_H     = 56;
export const HOURS      = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);

export function timeToMins(t) {
  if (!t) return null;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + (m || 0);
}

/* ════════════════════════════════════════════════════════
   AGRÉGATION + SCOPING (source unique, réutilisée par le dashboard)
   ════════════════════════════════════════════════════════ */

/** Normalise toutes les sources en un tableau d'items uniformes. */
export function buildAllItems({ tournaments, events, commEvents, publications, meetings, tasks, campagnes, requests, metricool, clubs, users }) {
  return [
    ...normalizeTournaments(tournaments, clubs),
    ...normalizeEvents(events, clubs, users),
    ...normalizeCommEvents(commEvents, clubs, users),
    ...normalizePublications(publications, clubs, users),
    ...normalizeMeetings(meetings, clubs, users),
    ...normalizeTasks(tasks, clubs, users),
    ...normalizeCampagnes(campagnes, clubs, users),
    ...normalizeRequests(requests, clubs, users),
    ...normalizeMetricool(metricool, clubs, users),
  ];
}

/** Filtre les items visibles par l'utilisateur (admin = tout ; sinon ses clubs + national). */
export function filterVisibleItems(items, currentUser, appId) {
  const isAdmin = currentUser?.admin === true;
  const userClubIds = new Set((currentUser?.clubs || []).map(String));
  return items.filter(it => visibleToUser(it, { isAdmin, userClubIds, appId }));
}
